<script setup lang="ts">
import { RouterLink, useRoute } from 'vue-router'
import { PhBookOpen, PhGear, PhGraduationCap, PhHouse, PhMoon, PhSun } from '@phosphor-icons/vue'
import { Button } from '@/components/ui/button'
import { useTheme } from '@/composables/useTheme'
import UserAuthMenu from '@/components/auth/UserAuthMenu.vue'

const route = useRoute()
const { isDark, toggleTheme } = useTheme()

const navItems = [
  { path: '/quran', label: 'Qur’an', icon: PhBookOpen },
  { path: '/learn', label: 'Learn', icon: PhGraduationCap },
]

const emit = defineEmits<{
  'open-settings': []
}>()

const openSettings = () => {
  emit('open-settings')
}
</script>

<template>
  <header
    class="sticky top-0 z-40 w-full border-b border-border bg-card/95 text-foreground shadow-sm backdrop-blur-xl transition-colors"
  >
    <div class="flex h-16 w-full items-center gap-2 px-4 sm:px-6 lg:px-10">
      <div class="flex shrink-0 items-center gap-2">
        <RouterLink
          to="/"
          class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-muted/50 text-foreground transition hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background md:hidden"
          aria-label="Home"
        >
          <PhHouse :size="22" />
        </RouterLink>

        <RouterLink
          to="/"
          class="hidden items-center gap-3 rounded-full border border-border bg-muted/50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.5em] text-muted-foreground transition hover:border-primary/35 hover:bg-primary/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background sm:flex"
        >
          Rattil
        </RouterLink>
      </div>

      <nav
        class="hidden min-w-0 flex-1 items-center justify-center gap-1 overflow-x-auto sm:gap-2 md:flex"
      >
        <RouterLink
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          :title="item.label"
          class="flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-2 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background sm:gap-2 sm:px-4 sm:text-sm"
          :class="
            route.path === item.path || route.path.startsWith(item.path + '/')
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          "
        >
          <component :is="item.icon" :size="18" />
          <span class="hidden sm:inline-block">{{ item.label }}</span>
        </RouterLink>
      </nav>

      <div class="ml-auto flex shrink-0 items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          class="rounded-full border border-border bg-background text-foreground hover:bg-muted"
          :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          @click="toggleTheme"
        >
          <PhSun v-if="isDark" :size="20" />
          <PhMoon v-else :size="20" />
        </Button>

        <UserAuthMenu />

        <Button
          variant="ghost"
          size="icon"
          class="rounded-full border border-border bg-background text-foreground hover:bg-muted"
          aria-label="Open settings"
          @click="openSettings"
        >
          <PhGear :size="20" />
        </Button>
      </div>
    </div>
  </header>
</template>
